import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  TextInput,
  Select,
  Button,
  Layer,
  Notification,
  Spinner,
} from "grommet";
import axios from "axios";
import AddCourseModal from "./AddCourseModal";
import { Add } from "grommet-icons";

const AddLearningPathModal = ({ portfolio_id, onClose, onCreated }) => {
  const [learningPathName, setLearningPathName] = useState("");
  const [coursesList, setCoursesList] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");

  const isValid = learningPathName.trim() && selectedCourseIds.length > 0;

  useEffect(() => {
    // Fetch all courses for this portfolio
    const fetchCourses = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `/api/admin/get-all-courses/${portfolio_id}`,
        );
        setCoursesList(res.data.courses || []);
      } catch (err) {
        console.error("Error fetching courses:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [portfolio_id]);

  const handleSubmit = async () => {
    if (!isValid) return;
    const payload = {
      learningPathName,
      courseIds: selectedCourseIds,
      portfolio_id: portfolio_id,
    };
    console.log("Create Learning Path payload:", payload);
    try {
      await axios.post(`/api/admin/add-learning-path`, payload);
      setNotificationMessage("New learning path added successfully!");
      setNotificationStatus("normal");
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
        onCreated();
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Error creating learning path:", err);
      setNotificationMessage("Failed to create learning path");
      setNotificationStatus("critical");
      setShowNotification(true);
    }
  };

  return (
    <Layer onEsc={onClose} onClickOutside={onClose} responsive>
      <Box pad="medium" gap="medium" width="large">
        {showNotification && (
          <Notification
            toast
            status={notificationStatus}
            message={notificationMessage}
            onClose={() => setShowNotification(false)}
          />
        )}
        <Heading level={3} margin="none">
          Add Learning Path
        </Heading>

        {/* Path Name */}
        <Box>
          <Text>
            Learning Path Name <Text color="red">*</Text>
          </Text>
          <TextInput
            placeholder="Enter learning path name"
            value={learningPathName}
            onChange={(e) => setLearningPathName(e.target.value)}
          />
        </Box>

        {/* Courses Multi-Select + Add Course Button */}
        <Box>
          <Text>
            Courses <Text color="red">*</Text>
          </Text>
          {loading ? (
            <Spinner />
          ) : (
            <Box direction="row" gap="small" align="center">
              <Select
                multiple
                options={coursesList}
                labelKey={(option) =>
                  `${option.course_c_id} - ${option.course_name}`
                }
                valueKey="course_id"
                placeholder="Select courses"
                value={selectedCourseIds}
                onChange={({ value }) => setSelectedCourseIds(value)}
                dropHeight="185px"
              />
              <Button
                icon={<Add />}
                label="Add New Course"
                secondary
                onClick={() => setShowAddCourseModal(true)}
              />
            </Box>
          )}
          {/* Show selected course names */}
          {selectedCourseIds.length > 0 && (
            <Box margin={{ top: "small" }}>
              <Text weight="bold">Selected Courses: </Text>
              <Text>
                {selectedCourseIds
                  .map((sel) => {
                    // if the select returns full objects
                    if (sel && typeof sel === "object") return sel.course_name;
                    // otherwise assume it's an ID
                    const found = coursesList.find((c) => c.course_id === sel);
                    return found?.course_name;
                  })
                  .filter(Boolean)
                  .join(", ")}
              </Text>
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box direction="row" gap="small" justify="end">
          <Button label="Cancel" onClick={onClose} />
          <Button
            label="Create"
            primary
            onClick={handleSubmit}
            disabled={!isValid}
          />
        </Box>
      </Box>

      {showAddCourseModal && (
        <Layer
          onEsc={() => setShowAddCourseModal(false)}
          onClickOutside={() => setShowAddCourseModal(false)}
        >
          <AddCourseModal
            portfolio_id={portfolio_id}
            onClose={() => setShowAddCourseModal(false)}
            onSubmit={(course) => {
              console.log("New course added:", course);
              // optionally refresh courses list
              setCoursesList((prev) => [...prev, course]);
              setShowAddCourseModal(false);
            }}
          />
        </Layer>
      )}
    </Layer>
  );
};

export default AddLearningPathModal;
